import { useApolloClient } from "@apollo/client";
import { ApplyItemAction } from "@msys/apply-item-action-isomorphic";
import { wizard } from "@msys/question-isomorphic";
import { ModalActions, ModalContent, ModalDialog } from "@msys/ui";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import {
  WizardSection,
  applyItemActionToApplyItemActionInput,
} from "../WizardSection.js";
import { useQuoteRefinementWizardModal_ApplyQuoteItemActionsMutation } from "./QuoteRefinementWizardModal.generated";
import { AskWhen, AskWhom } from "@msys/common";

export function QuoteRefinementWizardModal({
  projectId,
  quoteId,
  handleClose,
  startingNode,
  refetchAfterApplyItemAction,
  askWhen = "onQuoteRefinement",
  askWhom,
}: {
  projectId: string;
  quoteId: string;
  handleClose: () => void;
  startingNode: wizard.StartingNode;
  refetchAfterApplyItemAction?: () => Promise<void>;
  askWhen?: Extract<
    AskWhen,
    "onQuoteRefinement" | "onQuoteRefinement2" | "onQuoteRefinement3"
  >;
  askWhom: AskWhom;
}) {
  const { t } = useTranslate(["Decisions", "Global"]);

  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [applyQuoteItemActionsMutation] =
    useQuoteRefinementWizardModal_ApplyQuoteItemActionsMutation({ client });

  const [isCompleted, setIsCompleted] = React.useState(false);
  const [answers, setAnswers] = React.useState<Array<ApplyItemAction>>([]);

  const handleCloseWithRefetch = React.useCallback(() => {
    client.reFetchObservableQueries();
    refetchAfterApplyItemAction?.();
    handleClose();
  }, [client, handleClose, refetchAfterApplyItemAction]);

  return (
    <ModalDialog maxWidth="lg">
      {isCompleted ? (
        <ModalContent>
          <Stack
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <CheckCircleIcon color="success" sx={{ fontSize: 140 }} />
            <Typography
              variant={"h2"}
              sx={{
                color: "success.main",
              }}
            >
              {t("Your answers have been saved!", {
                ns: "Decisions",
              })}
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
              }}
            >
              {t(
                "Start the wizard again and you will be able to review any question that hasn't been finalised yet.",
                { ns: "Decisions" }
              )}
            </Typography>
          </Stack>
          <ModalActions
            actionButtons={[
              {
                label: t("Back", { ns: "Global" }),
                handleClick: () => {
                  setIsCompleted(false);
                },
                buttonProps: { variant: "outlined" },
              },
              {
                label: t("Close", { ns: "Global" }),
                handleClick: handleCloseWithRefetch,
                buttonProps: { variant: "contained" },
              },
            ]}
          />
        </ModalContent>
      ) : (
        <ModalContent>
          <WizardSection
            wizardConfiguration={{
              navigationType: null,
              questionStrategy: {
                type: "questionControl",
                askWhen,
                askWhom,
                startingNode,
              },
              requestInitialAnswers: "true",
              sourceDoc: {
                type: "quote",
                quoteId,
                contractorId: viewer.organisation.id,
              },
              showCloseButton: "true",
            }}
            initialAnswers={answers}
            onAnswer={async answers => {
              const mostRecentAnswer = answers[answers.length - 1];

              await applyQuoteItemActionsMutation({
                variables: {
                  input: {
                    applyItemActions: [
                      applyItemActionToApplyItemActionInput(
                        mostRecentAnswer.answer
                      ),
                    ],
                    projectId,
                    quoteId,
                  },
                },
              });
            }}
            onComplete={async result => {
              setIsCompleted(true);
              setAnswers(result.map(answer => answer.answer));
            }}
            onClose={handleCloseWithRefetch}
          />
        </ModalContent>
      )}
    </ModalDialog>
  );
}
